import React, { useState } from 'react';
import { PackageIcon } from 'lucide-react';
import { ACCOUNT_ORDERS, ACCOUNT_PROFILE } from '../data/account';
import { OrderStatus } from '../types';
import { formatPrice } from '../utils/format';
import { OrderCard } from '../components/account/OrderCard';
import { ProfileDataTab } from '../components/account/ProfileDataTab';
import { AddressesTab } from '../components/account/AddressesTab';
import { useScreenInit } from '../useScreenInit.js';
type Tab = 'pedidos' | 'datos' | 'direcciones';
const TABS: {
  id: Tab;
  label: string;
}[] = [{
  id: 'pedidos',
  label: 'Mis pedidos'
}, {
  id: 'datos',
  label: 'Mis datos'
}, {
  id: 'direcciones',
  label: 'Direcciones'
}];
const FILTERS: {
  id: OrderStatus | 'todos';
  label: string;
}[] = [{
  id: 'todos',
  label: 'Todos'
}, {
  id: 'pendiente',
  label: 'Pendiente'
}, {
  id: 'proceso',
  label: 'En proceso'
}, {
  id: 'enviado',
  label: 'Enviado'
}, {
  id: 'entregado',
  label: 'Entregado'
}];
export function Account() {
  const screenInit = useScreenInit();
  const [tab, setTab] = useState<Tab>(screenInit.tab as Tab ?? 'pedidos');
  const [filter, setFilter] = useState<OrderStatus | 'todos'>('todos');
  const orders = filter === 'todos' ? ACCOUNT_ORDERS : ACCOUNT_ORDERS.filter(o => o.status === filter);
  return <div className="w-full bg-black" data-id="element-702" data-mp="2t3ivng43ifi1">
      <div className="mx-auto w-full max-w-6xl px-5 py-10 lg:px-8 lg:py-14" data-id="element-703" data-mp="2t2hjt9f99oz9">
        {/* PROFILE CARD */}
        <section className="rounded-xl border border-white/10 bg-ink-800 p-6 sm:p-8" data-id="element-704" data-mp="2o57ppggx8p53">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-705" data-mp="2j38lk4dcxo73">
            <div className="flex items-center gap-5" data-id="element-706" data-mp="2e68ouk2edxsn">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-pink-500 font-display text-2xl tracking-wider text-white" aria-hidden data-id="element-707" data-mp="2a4euetispq0x">
                {ACCOUNT_PROFILE.initials}
              </span>
              <div data-id="element-708" data-mp="21vhei2rvbyim">
                <h1 className="font-display text-3xl uppercase leading-none tracking-wide text-white sm:text-4xl" data-id="element-709" data-mp="21uza2iv1ydzm">
                  Hola, {ACCOUNT_PROFILE.name}
                </h1>
                <p className="mt-2 text-sm text-white/40" data-id="element-710" data-mp="1sut21ci4i67u">
                  Miembro desde {ACCOUNT_PROFILE.since}
                </p>
              </div>
            </div>

            <dl className="grid grid-cols-3 gap-6 border-t border-white/10 pt-6 sm:border-l sm:border-t-0 sm:pl-8 sm:pt-0" data-id="element-711" data-mp="1wwo2yfw56hl3">
              <div data-id="element-712" data-mp="2pl3akhcy1xme">
                <dd className="font-display text-2xl leading-none text-white" data-id="element-713" data-mp="2pllgk5perq7i">
                  {ACCOUNT_PROFILE.orders}
                </dd>
                <dt className="mt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40" data-id="element-714" data-mp="2bl0xr9jhtue0">
                  Pedidos
                </dt>
              </div>
              <div data-id="element-715" data-mp="1053vkjmn9dyu">
                <dd className="font-display text-2xl leading-none text-white" data-id="element-716" data-mp="104kgdaodp3oi">
                  {formatPrice(ACCOUNT_PROFILE.spent)}
                </dd>
                <dt className="mt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40" data-id="element-717" data-mp="wnokwt4rw97b">
                  Gastados
                </dt>
              </div>
              <div data-id="element-718" data-mp="soyx06m3z99f">
                <dd className="font-display text-2xl leading-none text-pink-500" data-id="element-719" data-mp="16m5ii8y7vm2c">
                  VIP
                </dd>
                <dt className="mt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40" data-id="element-720" data-mp="11occbfwwys22">
                  {ACCOUNT_PROFILE.tier}
                </dt>
              </div>
            </dl>
          </div>
        </section>

        {/* TABS */}
        <div role="tablist" aria-label="Secciones de mi cuenta" className="mt-10 flex gap-8 border-b border-white/10" data-id="element-721" data-mp="nnjcqgrr96s3">
          {TABS.map(item => {
          const active = tab === item.id;
          return <button key={item.id} type="button" role="tab" aria-selected={active} onClick={() => setTab(item.id)} className={`relative pb-4 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${active ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-722" data-mp="3rb1u3uz670hn">
                {item.label}
                <span className={`absolute -bottom-[1px] left-0 h-[2px] w-full transition-colors ${active ? 'bg-pink-500' : 'bg-transparent'}`} data-id="element-723" data-mp="33db63dp7gypu" />
              </button>;
        })}
        </div>

        {/* ORDERS */}
        {tab === 'pedidos' && <div className="mt-8" data-id="element-724" data-mp="3ic54gwi0fdwb">
            <div className="flex flex-wrap gap-2" data-id="element-725" data-mp="3i8ps41fq0iyl">
              {FILTERS.map(f => <button key={f.id} type="button" onClick={() => setFilter(f.id)} aria-pressed={filter === f.id} className={`rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${filter === f.id ? 'border-pink-500 bg-pink-500 text-white' : 'border-white/15 text-white/50 hover:border-white/40 hover:text-white'}`} data-id="element-726" data-mp="3mab99yzafjel">
                  {f.label}
                </button>)}
            </div>

            {orders.length === 0 ? <div className="mt-8 border border-dashed border-white/15 px-6 py-20 text-center" data-id="element-727" data-mp="23cfyr7vztxxs">
                <PackageIcon className="mx-auto h-9 w-9 text-white/20" data-id="element-728" data-mp="1yfxzgnjfmrsu" />
                <h2 className="mt-5 font-display text-2xl uppercase text-white" data-id="element-729" data-mp="1uc9so72mk08n">
                  Sin pedidos en este estado
                </h2>
                <p className="mx-auto mt-2 max-w-sm text-sm text-white/45" data-id="element-730" data-mp="1pdondt8imqdc">
                  Cambiá el filtro para ver el resto de tus compras.
                </p>
              </div> : <div className="mt-6 space-y-5" data-id="element-731" data-mp="e0w12xyhuu4o">
                {orders.map((order, i) => <OrderCard key={order.id} order={order} defaultExpanded={i === 0 && filter === 'todos'} data-id="element-732" data-mp="fgrn426968nk" data-mpi={i} />)}
              </div>}
          </div>}

        {tab === 'datos' && <div className="mt-8 max-w-2xl" data-id="element-733" data-mp="af2cuoqutz0j">
            <ProfileDataTab data-id="element-734" data-mp="3sv4tgsiatg29" />
          </div>}

        {tab === 'direcciones' && <div className="mt-8 max-w-3xl" data-id="element-735" data-mp="1fxepjj7g7ow">
            <AddressesTab data-id="element-736" data-mp="1gz1umdhwkmz" />
          </div>}
      </div>
    </div>;
}